import React, { useState } from 'react';
import { bookings } from '../../api';
import './Bookings.css';

export default function JoinTypeWaitlistModal({ isOpen, onClose, coachId, onSuccess }) {
  const [selectedType, setSelectedType] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const lessonTypes = [
    { value: 'technique', label: '🎾 Technique Training', description: 'Focus on specific strokes and form' },
    { value: 'match-play', label: '🏆 Match Play', description: 'Practice match scenarios and strategies' },
    { value: 'drill-practice', label: '⚡ Drill Practice', description: 'Repetitive drills to build consistency' },
    { value: 'match-prep', label: '📋 Match Preparation', description: 'Prepare for upcoming matches' },
    { value: 'mental', label: '🧠 Mental Training', description: 'Sports psychology and mental toughness' },
    { value: 'custom', label: '✨ Other', description: 'Custom coaching session' }
  ];

  const handleSubmit = async () => {
    if (!selectedType) {
      setError('Please select a lesson type');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await bookings.joinTypeWaitlist(coachId, selectedType, notes || null);

      // Reset form
      setSelectedType('');
      setNotes('');

      // Show success and close
      onSuccess?.();
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('❌ Error joining waitlist:', err);
      setError(err.response?.data?.message || 'Failed to join waitlist');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0, marginBottom: '5px' }}>
          📋 Join Lesson Type Waitlist
        </h3>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
          You'll be notified when a spot opens up for your selected lesson type
        </p>

        <div style={{
          fontSize: '12px',
          color: '#666',
          background: '#f0f7ff',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '15px',
          borderLeft: '3px solid #3498db'
        }}>
          <strong>🎯 Smart Matching:</strong>
          <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
            <li>Technique, Match Play, Drill & Match Prep: Matched within your skill level (±1.0 rating)</li>
            <li>Mental & Custom: Any skill level welcome</li>
            <li>The first person to join sets the skill level - you'll match with others at that level</li>
          </ul>
        </div>

        {error && (
          <div
            style={{
              background: '#fee',
              color: '#c33',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '15px',
              fontSize: '13px'
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {/* Lesson Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '10px' }}>
            Select Lesson Type:
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lessonTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => {
                  setSelectedType(type.value);
                  setError('');
                }}
                style={{
                  padding: '12px 15px',
                  background: selectedType === type.value ? '#2f4ba2' : '#f0f0f0',
                  color: selectedType === type.value ? 'white' : '#333',
                  border: selectedType === type.value ? '2px solid #2f4ba2' : '2px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '3px' }}>
                  {type.label}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  {type.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Optional Notes */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            Optional Notes:
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., 'Interested in Monday or Wednesday sessions'"
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '13px',
              fontFamily: 'inherit',
              minHeight: '60px',
              resize: 'vertical'
            }}
          />
          <div style={{ fontSize: '11px', color: '#999', marginTop: '5px' }}>
            {notes.length}/500 characters
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              background: '#ccc',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedType}
            style={{
              padding: '10px 20px',
              background: isSubmitting || !selectedType ? '#999' : '#2f4ba2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubmitting || !selectedType ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            {isSubmitting ? '⏳ Joining...' : '📋 Join Waitlist'}
          </button>
        </div>
      </div>
    </div>
  );
}
